import { createContext, FC, ReactNode, useEffect, useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref } from 'firebase/database';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAuthService, apiNotificationService } from '@api';
import { EEventName, TNotification } from '@xeppt/xeppt-sdk/types/notification';
import { useObjectVal } from 'react-firebase-hooks/database';
import { useUserContext } from '@hooks/context/useUserContext';
import notificationSound from '@audio/new_notification.mp3';
import { ENewNotificationType } from '@enum';
import { getNotificationTypeByEvent } from '@utils/notifications';
import { useVerificationResult } from '@hooks/api/useVerificationResult';
import { useIsFirstRender } from '@mantine/hooks';
import { EUserStatus } from '@xeppt/xeppt-sdk/types/user';

type TNotificationsContext = {
    database?: any;
    notifications: TNotification[];
    isNotificationsLoading: boolean;
    newNotificationType?: ENewNotificationType;
    lastNotificationType?: EEventName;
    refetchNotifications: () => void;
    triggerNotificationType: (value: ENewNotificationType) => void;
    handleGetVerificationResult: (
        codes: string[],
        status: EUserStatus,
        ignoreNotification?: boolean
    ) => void;
    loadMoreNotifications: () => void;
    totalNotifications: number;
    unreadNotifications: number;
    notificationsCursor?: Date;
};

export const NotificationsContext = createContext<TNotificationsContext>({
    refetchNotifications: () => {},
    loadMoreNotifications: () => {},
    triggerNotificationType: () => {},
    handleGetVerificationResult: () => {},
    isNotificationsLoading: false,
    notifications: [],
    totalNotifications: 0,
    unreadNotifications: 0
});

interface IProps {
    children: ReactNode;
}

export const NotificationsContextProvider: FC<IProps> = ({ children }) => {
    const [database, setDatabase] = useState<any>();
    const { user, refetchAccount, refetchUser, handleLogout } = useUserContext();
    const [isSecondRender, setIsSecondRender] = useState(false);
    const isFirstRender = useIsFirstRender();
    const [newNotificationType, setNewNotificationType] = useState<ENewNotificationType>();
    const [newNotification] = useObjectVal(database ? ref(database, user?.id) : null);
    const { handleGetVerificationResult } = useVerificationResult();
    const [totalNotifications, setTotalNotifications] = useState(0);
    const [unreadNotifications, setUnreadNotifications] = useState(0);
    const [notificationsCursor, setNotificationsCursor] = useState<Date>();
    const [notifications, setNotifications] = useState<TNotification[]>([]);
    const [lastNotificationType, setLastNotificationType] = useState<EEventName>();

    const playNotificationsSound = () => {
        const audio = new Audio(notificationSound);
        audio.play();
    };

    const { data: firebaseConfig } = useApiQuery({
        method: () => apiAuthService.getRDBCredentials(),
        condition: !!user,
        deps: [user]
    });

    const { isLoading: isNotificationsLoading, handleRequest: refetchNotifications } = useApiQuery({
        method: (request?: { isRefresh?: boolean; isMore?: boolean }) => {
            if (!user)
                return Promise.resolve({ total: 0, cursor: new Date(), data: [], total_unread: 0 });
            return apiNotificationService.getNotifications({
                limit: 50,
                cursor: request?.isRefresh ? undefined : notificationsCursor
            });
        },
        isInitialRequest: false,
        condition: !!user,
        deps: [user],
        onSuccess: ({ total, cursor, data, total_unread }, request) => {
            if (notificationsCursor && request?.isMore) {
                setNotifications((state) => [...state, ...data]);
            } else {
                setNotifications(data);
            }
            setNotificationsCursor(cursor);
            setTotalNotifications(total);
            setUnreadNotifications(total_unread);
        }
    });

    const handleUpdateNotificationType = (event: EEventName) => {
        const resultKey = getNotificationTypeByEvent(event);
        setNewNotificationType(resultKey);
        if (resultKey) {
            setTimeout(() => {
                setNewNotificationType(undefined);
            }, 1000);
        }
    };

    useEffect(() => {
        if (!isFirstRender) {
            setIsSecondRender(true);
        }
        //@ts-ignore
        if (isSecondRender) {
            playNotificationsSound();
            refetchNotifications({ isRefresh: true });
            //@ts-ignore
            handleUpdateNotificationType(newNotification?.data?.MsgID as EEventName);
            //@ts-ignore
            setLastNotificationType(newNotification?.data?.MsgID as EEventName);
            //@ts-ignore
            if (newNotification?.data?.MsgID === EEventName.UserVerified) {
                handleGetVerificationResult(
                    //@ts-ignore
                    newNotification?.data?.Codes?.split(',').filter(Boolean),
                    EUserStatus.ACTIVE
                );
            }
            //@ts-ignore
            if (newNotification?.data?.MsgID === EEventName.UserUnVerified) {
                handleGetVerificationResult(
                    //@ts-ignore
                    newNotification?.data?.Codes?.split(',').filter(Boolean),
                    //@ts-ignore
                    EUserStatus.UNVERIFIED
                );
            }
        }
    }, [newNotification]);

    useEffect(() => {
        if (firebaseConfig) {
            const firebaseApp = initializeApp(JSON.parse(firebaseConfig));
            //@ts-ignore
            setDatabase(getDatabase(firebaseApp));
        }
    }, [firebaseConfig]);

    const loadMoreNotifications = () => {
        refetchNotifications({ isMore: true });
    };

    const refreshNotifications = () => {
        setNotificationsCursor(undefined);
        setTotalNotifications(0);
        setNotifications([]);
        refetchNotifications({ isRefresh: true });
    };

    useEffect(() => {
        setTimeout(() => {
            if (newNotificationType === ENewNotificationType.USER) {
                setTimeout(() => {
                    refetchUser();
                }, 1000);
            }
            if (
                newNotificationType === ENewNotificationType.ACCOUNT ||
                newNotificationType === ENewNotificationType.TRANSACTION ||
                newNotificationType === ENewNotificationType.CARD
            ) {
                refetchAccount();
            }
            if (newNotificationType === ENewNotificationType.LOGOUT) {
                handleLogout();
            }
        }, 500);
    }, [newNotificationType]);

    return (
        <NotificationsContext.Provider
            value={{
                database,
                notifications: notifications,
                isNotificationsLoading,
                refetchNotifications: refreshNotifications,
                newNotificationType,
                lastNotificationType,
                loadMoreNotifications,
                handleGetVerificationResult,
                totalNotifications,
                notificationsCursor,
                unreadNotifications,
                triggerNotificationType: (value: ENewNotificationType) => {
                    setNewNotificationType(value);
                    setTimeout(() => {
                        setNewNotificationType(undefined);
                    }, 1000);
                }
            }}>
            {children}
        </NotificationsContext.Provider>
    );
};
